import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TrialBanner from '../components/TrialBanner';
import ConnectedProviders from '../components/ConnectedProviders';
import {
  DocumentTextIcon, ArrowUpTrayIcon, ChartBarIcon,
  ExclamationTriangleIcon, CheckCircleIcon, ClockIcon,
  ClipboardDocumentListIcon, BeakerIcon, ShieldCheckIcon, LockClosedIcon,
  SparklesIcon, TrashIcon,
} from '@heroicons/react/24/outline';

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get('/analysis').catch(() => ({ data: [] })),
      api.get('/trends').catch(() => ({ data: [] })),
    ]).then(([sessRes, trendRes]) => {
      setSessions(sessRes.data);
      setTrends(trendRes.data);
    }).finally(() => setLoading(false));
  }, []);

  const handleDelete = useCallback(async (e, sessionId) => {
    e.stopPropagation();
    if (!window.confirm('Delete this report and its analysis? This cannot be undone.')) return;
    setDeletingId(sessionId);
    try {
      await api.delete(`/analysis/${sessionId}`);
      setSessions(prev => prev.filter(s => s.id !== sessionId));
    } catch {
      alert('Could not delete this report. Please try again.');
    } finally {
      setDeletingId(null);
    }
  }, []);

  const urgentAlerts = trends.filter(t => t.hasAlert);
  const recentSession = sessions[0];

  if (loading) return <div className="flex items-center justify-center h-64 text-slate-400">Loading your health summary…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <TrialBanner />
      {/* Welcome */}
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-slate-800 dark:text-white">Welcome back, {user?.name?.split(' ')[0]} 👋</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">Here's your health overview.</p>
      </div>

      {/* Alerts */}
      {urgentAlerts.length > 0 && (
        <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Trending Alerts</p>
              <ul className="mt-1 space-y-1">
                {urgentAlerts.slice(0, 3).map((t, i) => (
                  <li key={i} className="text-sm text-amber-700 dark:text-amber-400">{t.alertMessage}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Quick actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <Link
          to="/app/upload"
          className="bg-sky-600 hover:bg-sky-700 text-white rounded-xl p-5 flex items-center gap-4 transition-colors shadow-sm"
        >
          <ArrowUpTrayIcon className="w-7 h-7 flex-shrink-0" />
          <div>
            <p className="font-semibold">Upload New Report</p>
            <p className="text-sky-100 text-sm">PDF or text supported</p>
          </div>
        </Link>

        <Link
          to="/app/trends"
          className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex items-center gap-4 transition-colors shadow-sm"
        >
          <ChartBarIcon className="w-7 h-7 text-sky-500 flex-shrink-0" />
          <div>
            <p className="font-semibold text-slate-700 dark:text-slate-200">View Trends</p>
            <p className="text-slate-400 dark:text-slate-500 text-sm">{trends.length} tracked values</p>
          </div>
        </Link>

        {recentSession ? (
          <button
            onClick={() => navigate(`/app/analysis/${recentSession.id}`)}
            className="bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex items-center gap-4 transition-colors shadow-sm text-left"
          >
            <DocumentTextIcon className="w-7 h-7 text-indigo-500 flex-shrink-0" />
            <div>
              <p className="font-semibold text-slate-700 dark:text-slate-200">Last Analysis</p>
              <p className="text-slate-400 dark:text-slate-500 text-sm">{new Date(recentSession.createdAt).toLocaleDateString()}</p>
            </div>
          </button>
        ) : (
          <div className="bg-white dark:bg-slate-800 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-5 flex items-center gap-4">
            <DocumentTextIcon className="w-7 h-7 text-slate-300 dark:text-slate-600 flex-shrink-0" />
            <div>
              <p className="font-medium text-slate-400 dark:text-slate-500">No analyses yet</p>
              <p className="text-slate-300 dark:text-slate-600 text-sm">Upload your first report</p>
            </div>
          </div>
        )}
      </div>

      {/* Premium features section */}
      {(() => {
        const isActive = user?.subscriptionStatus === 'active';
        const premiumFeatures = [
          {
            to: '/app/appointment-prep',
            icon: ClipboardDocumentListIcon,
            title: 'Appointment Prep Brief',
            desc: 'AI-generated summary of your labs to bring to your doctor.',
            color: 'sky',
          },
          {
            to: '/app/medication-check',
            icon: BeakerIcon,
            title: 'Medication Interaction Check',
            desc: 'Check your medications for interactions and lab-based flags.',
            color: 'teal',
          },
          {
            to: '/app/care-gaps',
            icon: ShieldCheckIcon,
            title: 'Preventive Care Gaps',
            desc: 'Screenings and tests you may be missing based on guidelines.',
            color: 'green',
          },
        ];
        return (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-3">
              <SparklesIcon className="w-4 h-4 text-sky-500" />
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Premium Features</h2>
              {!isActive && (
                <Link to="/pricing" className="ml-auto text-xs font-medium text-sky-600 hover:text-sky-700 bg-sky-50 border border-sky-200 px-2.5 py-1 rounded-full transition-colors">
                  Unlock for $25/mo →
                </Link>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {premiumFeatures.map(({ to, icon: Icon, title, desc, color }) =>
                isActive ? (
                  <Link
                    key={to}
                    to={to}
                    className={`bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:border-${color}-300 hover:bg-${color}-50/30 rounded-xl p-5 flex flex-col gap-3 transition-all shadow-sm group`}
                  >
                    <div className={`w-9 h-9 rounded-lg bg-${color}-100 flex items-center justify-center`}>
                      <Icon className={`w-5 h-5 text-${color}-600`} />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-700 dark:text-slate-200 text-sm group-hover:text-slate-900 dark:group-hover:text-white">{title}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{desc}</p>
                    </div>
                  </Link>
                ) : (
                  <Link
                    key={to}
                    to="/pricing"
                    className="bg-slate-50 dark:bg-slate-800/50 border border-dashed border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col gap-3 opacity-70 hover:opacity-90 transition-opacity relative overflow-hidden"
                  >
                    <div className="absolute top-3 right-3">
                      <LockClosedIcon className="w-4 h-4 text-slate-400" />
                    </div>
                    <div className="w-9 h-9 rounded-lg bg-slate-200 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-slate-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-500 dark:text-slate-400 text-sm">{title}</p>
                      <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{desc}</p>
                    </div>
                  </Link>
                )
              )}
            </div>
          </div>
        );
      })()}

      {/* Connected Health Providers */}
      <ConnectedProviders isSubscriber={user?.subscriptionStatus === 'active'} />

      {/* Recent sessions */}
      <div>
        <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-3">Recent Reports</h2>
        {sessions.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center">
            <ArrowUpTrayIcon className="w-10 h-10 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400 dark:text-slate-500">No reports analyzed yet.</p>
            <Link to="/app/upload" className="mt-3 inline-block text-sky-600 font-medium text-sm hover:underline">Upload your first lab report →</Link>
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.slice(0, 5).map((s) => (
              <div
                key={s.id}
                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4 flex items-center gap-4 hover:border-sky-200 dark:hover:border-sky-700 hover:bg-sky-50/30 dark:hover:bg-sky-900/20 transition-all"
              >
                <button
                  onClick={() => navigate(`/app/analysis/${s.id}`)}
                  className="flex-1 min-w-0 text-left"
                >
                  <p className="font-medium text-slate-700 dark:text-slate-200 text-sm">
                    {s.labReport?.labProvider || 'Lab Report'} — {s.labReport?.reportDate ? new Date(s.labReport.reportDate).toLocaleDateString() : 'Unknown date'}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                    Analyzed {new Date(s.createdAt).toLocaleDateString()} · {s.language?.toUpperCase()} · {s.literacyLevel} level
                  </p>
                </button>
                {s.comprehensionScore !== null ? (
                  <div className="flex items-center gap-1.5 text-emerald-600">
                    <CheckCircleIcon className="w-4 h-4" />
                    <span className="text-xs font-medium">{s.comprehensionScore}% understood</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 text-slate-400">
                    <ClockIcon className="w-4 h-4" />
                    <span className="text-xs">Quiz pending</span>
                  </div>
                )}
                <button
                  onClick={(e) => handleDelete(e, s.id)}
                  disabled={deletingId === s.id}
                  className="ml-1 p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
                  title="Delete report"
                >
                  {deletingId === s.id
                    ? <span className="text-xs text-slate-400">…</span>
                    : <TrashIcon className="w-4 h-4" />}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
